import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Webhook, Key, Plus, Copy, Trash2, Users, FileText, Activity,
  CheckCircle2, XCircle, ExternalLink, Code, RefreshCw, Globe, Link2, Phone
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Layout from "../components/Layout";
import { API, BACKEND_URL } from "../App";
import { toast } from "sonner";

const LeadCaptureSettingsPage = () => {
  const [webhookKeys, setWebhookKeys] = useState([]);
  const [partners, setPartners] = useState([]);
  const [formTokens, setFormTokens] = useState([]);
  const [activity, setActivity] = useState({ activity: [], total: 0 });
  const [loading, setLoading] = useState(true);

  // Forms
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeySource, setNewKeySource] = useState("webhook");
  const [newPartnerName, setNewPartnerName] = useState("");
  const [newPartnerCompany, setNewPartnerCompany] = useState("");
  const [newPartnerPhone, setNewPartnerPhone] = useState("");
  const [newPartnerEmail, setNewPartnerEmail] = useState("");
  const [newTokenName, setNewTokenName] = useState("");
  const [newTokenSource, setNewTokenSource] = useState("website_form");

  const BASE_WEBHOOK_URL = `${BACKEND_URL}/api/leads/webhook`;

  const fetchAll = async () => {
    try {
      const [keysRes, partnersRes, tokensRes, activityRes] = await Promise.all([
        axios.get(`${API}/leads/webhook/keys`),
        axios.get(`${API}/leads/webhook/partners`),
        axios.get(`${API}/leads/webhook/form-tokens`),
        axios.get(`${API}/leads/webhook/activity?limit=30`)
      ]);
      setWebhookKeys(keysRes.data);
      setPartners(partnersRes.data);
      setFormTokens(tokensRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const createWebhookKey = async () => {
    if (!newKeyName.trim()) { toast.error("Key name required"); return; }
    try {
      const res = await axios.post(`${API}/leads/webhook/keys`, {
        name: newKeyName, default_source: newKeySource
      });
      toast.success(`API key created: ${res.data.key.substring(0, 12)}...`);
      setNewKeyName(""); setNewKeySource("webhook");
      fetchAll();
    } catch (error) {
      toast.error("Failed to create key");
    }
  };

  const revokeKey = async (keyId) => {
    try {
      await axios.delete(`${API}/leads/webhook/keys/${keyId}`);
      toast.success("Key revoked");
      fetchAll();
    } catch (error) {
      toast.error("Failed to revoke key");
    }
  };

  const createPartner = async () => {
    if (!newPartnerName.trim()) { toast.error("Partner name required"); return; }
    try {
      await axios.post(`${API}/leads/webhook/partners`, {
        name: newPartnerName, company: newPartnerCompany,
        phone: newPartnerPhone, email: newPartnerEmail
      });
      toast.success("Partner added — pending manager approval");
      setNewPartnerName(""); setNewPartnerCompany("");
      setNewPartnerPhone(""); setNewPartnerEmail("");
      fetchAll();
    } catch (error) {
      toast.error("Failed to create partner");
    }
  };

  const approvePartner = async (partnerId, action) => {
    try {
      await axios.put(`${API}/leads/webhook/partners/${partnerId}/approve`, { action });
      toast.success(action === "approve" ? "Partner approved" : "Partner rejected");
      fetchAll();
    } catch (error) {
      toast.error("Failed to update partner");
    }
  };

  const togglePartner = async (partnerId, active) => {
    try {
      await axios.put(`${API}/leads/webhook/partners/${partnerId}`, { active: !active });
      toast.success(active ? "Partner deactivated" : "Partner activated");
      fetchAll();
    } catch (error) {
      toast.error("Failed to update partner");
    }
  };

  const createFormToken = async () => {
    if (!newTokenName.trim()) { toast.error("Token name required"); return; }
    try {
      await axios.post(`${API}/leads/webhook/form-tokens`, {
        name: newTokenName, source: newTokenSource
      });
      toast.success("Form token created");
      setNewTokenName(""); setNewTokenSource("website_form");
      fetchAll();
    } catch (error) {
      toast.error("Failed to create token");
    }
  };

  const getEmbedCode = (token) => {
    return `<!-- PropertyCRM Lead Capture Form -->
<form id="propertyCrmForm" onsubmit="return submitLeadForm(event)">
  <input type="text" name="name" placeholder="Full Name" required />
  <input type="tel" name="phone" placeholder="Phone Number" required />
  <input type="email" name="email" placeholder="Email" />
  <select name="configuration">
    <option value="">Select BHK</option>
    <option value="1BHK">1 BHK</option>
    <option value="2BHK">2 BHK</option>
    <option value="3BHK">3 BHK</option>
  </select>
  <textarea name="message" placeholder="Message"></textarea>
  <button type="submit">Submit Enquiry</button>
</form>
<script>
async function submitLeadForm(e) {
  e.preventDefault();
  const fd = new FormData(e.target);
  const res = await fetch('${BASE_WEBHOOK_URL}/form', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      form_token: '${token}',
      name: fd.get('name'),
      phone: fd.get('phone'),
      email: fd.get('email'),
      configuration: fd.get('configuration'),
      message: fd.get('message')
    })
  });
  if (res.ok) alert('Thank you! We will contact you soon.');
  else alert('Error submitting form.');
}
</script>`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="lead-capture-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Webhook className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Lead Capture</h1>
              <p className="text-slate-500">Webhook URLs, API keys, partners, and embeddable forms</p>
            </div>
          </div>
        </div>

        {/* Webhook URLs Reference */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" /> Webhook Endpoints
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm font-mono">
              {[
                { label: "Universal", url: BASE_WEBHOOK_URL },
                { label: "Meta/Facebook Ads", url: `${BASE_WEBHOOK_URL}/meta` },
                { label: "Google Ads", url: `${BASE_WEBHOOK_URL}/google` },
                { label: "Property Portal", url: `${BASE_WEBHOOK_URL}/portal` },
                { label: "IVR / Incoming Call", url: `${BACKEND_URL}/api/ivr/incoming` },
              ].map((ep) => (
                <div key={ep.label} className="flex items-center gap-2 bg-white/10 rounded-lg p-2.5">
                  <span className="text-cyan-300 text-xs shrink-0">{ep.label}:</span>
                  <span className="truncate text-white/80 flex-1">{ep.url}</span>
                  <button onClick={() => copyToClipboard(ep.url)} className="text-white/50 hover:text-white shrink-0">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-white/60 text-xs mt-3">All webhook endpoints require <code className="bg-white/10 px-1 rounded">X-API-Key</code> header (except partner and form endpoints)</p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="keys" className="w-full">
          <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1 max-w-2xl">
            <TabsTrigger value="keys" data-testid="keys-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              <Key className="w-4 h-4 mr-1" /> API Keys
            </TabsTrigger>
            <TabsTrigger value="partners" data-testid="partners-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              <Users className="w-4 h-4 mr-1" /> Partners
            </TabsTrigger>
            <TabsTrigger value="forms" data-testid="forms-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              <Code className="w-4 h-4 mr-1" /> Forms
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="activity-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              <Activity className="w-4 h-4 mr-1" /> Activity
            </TabsTrigger>
            <TabsTrigger value="ivr" data-testid="ivr-tab" className="text-xs whitespace-nowrap px-3 py-2 flex-shrink-0">
              <Phone className="w-4 h-4 mr-1" /> IVR Setup
            </TabsTrigger>
          </TabsList>

          {/* API Keys Tab */}
          <TabsContent value="keys" className="mt-6 space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Create API Key</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-3">
                <Input
                  placeholder="Key name (e.g. Meta Ads)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  data-testid="new-key-name"
                />
                <Input
                  placeholder="Default source"
                  value={newKeySource}
                  onChange={(e) => setNewKeySource(e.target.value)}
                  className="sm:max-w-[200px]"
                  data-testid="new-key-source"
                />
                <Button onClick={createWebhookKey} data-testid="create-key-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create
                </Button>
              </CardContent>
            </Card>

            {webhookKeys.length > 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {webhookKeys.map((k) => (
                      <div key={k.key_id} className="flex items-center justify-between p-4" data-testid={`key-${k.key_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{k.name}</p>
                            {k.active ? (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Active</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-red-500"><XCircle className="w-3 h-3" /> Revoked</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 font-mono mt-0.5">{k.key?.substring(0, 20)}...</p>
                          <p className="text-xs text-slate-400">Source: {k.default_source}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(k.key)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          {k.active && (
                            <Button variant="ghost" size="sm" onClick={() => revokeKey(k.key_id)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  No API keys yet. Create one to start receiving webhook leads.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="mt-6 space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Add Channel Partner</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Partner name *" value={newPartnerName} onChange={(e) => setNewPartnerName(e.target.value)} data-testid="new-partner-name" />
                  <Input placeholder="Company" value={newPartnerCompany} onChange={(e) => setNewPartnerCompany(e.target.value)} />
                  <Input placeholder="Phone" value={newPartnerPhone} onChange={(e) => setNewPartnerPhone(e.target.value)} />
                  <Input placeholder="Email" value={newPartnerEmail} onChange={(e) => setNewPartnerEmail(e.target.value)} />
                </div>
                <Button onClick={createPartner} data-testid="create-partner-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Partner
                </Button>
              </CardContent>
            </Card>

            {partners.length > 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {partners.map((p) => (
                      <div key={p.partner_id} className="flex items-center justify-between p-4" data-testid={`partner-${p.partner_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{p.name}</p>
                            {p.approval_status === "pending" ? (
                              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Pending Approval</span>
                            ) : p.approval_status === "rejected" ? (
                              <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Rejected</span>
                            ) : p.active ? (
                              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                            ) : (
                              <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{p.company}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span>Code: <code className="bg-slate-100 px-1.5 py-0.5 rounded font-semibold text-slate-700">{p.code}</code></span>
                            {p.leads_submitted > 0 && <span>{p.leads_submitted} leads</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.approval_status === "pending" && (
                            <>
                              <Button variant="outline" size="sm" data-testid={`approve-partner-${p.partner_id}`} onClick={() => approvePartner(p.partner_id, "approve")} className="text-emerald-600 hover:text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button variant="outline" size="sm" data-testid={`reject-partner-${p.partner_id}`} onClick={() => approvePartner(p.partner_id, "reject")} className="text-red-500 hover:text-red-700 border-red-200 hover:bg-red-50">
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {p.active && p.approval_status !== "pending" && (
                            <Button variant="outline" size="sm" data-testid={`copy-portal-link-${p.partner_id}`} onClick={() => copyToClipboard(`${window.location.origin}/cp/${p.code}`)} className="text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50">
                              <Link2 className="w-4 h-4 mr-1.5" /> Copy Portal Link
                            </Button>
                          )}
                          {p.approval_status !== "pending" && (
                            <Button variant="ghost" size="sm" onClick={() => togglePartner(p.partner_id, p.active)} className={p.active ? "text-red-500" : "text-emerald-600"}>
                              {p.active ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  No channel partners yet. Add one to let them submit leads.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Forms Tab */}
          <TabsContent value="forms" className="mt-6 space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Create Form Token</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-3">
                <Input placeholder="Form name (e.g. Homepage)" value={newTokenName} onChange={(e) => setNewTokenName(e.target.value)} data-testid="new-token-name" />
                <Input placeholder="Source tag" value={newTokenSource} onChange={(e) => setNewTokenSource(e.target.value)} className="sm:max-w-[200px]" />
                <Button onClick={createFormToken} data-testid="create-token-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create
                </Button>
              </CardContent>
            </Card>

            {formTokens.length > 0 ? (
              <div className="space-y-4">
                {formTokens.map((t) => (
                  <Card key={t.token_id} className="bg-white border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-900">{t.name}</p>
                          <p className="text-sm text-slate-500 font-mono">{t.token?.substring(0, 20)}...</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(getEmbedCode(t.token))} data-testid={`copy-embed-${t.token_id}`}>
                          <Code className="w-4 h-4 mr-2" /> Copy Embed Code
                        </Button>
                      </div>
                      <details className="text-sm">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800">View embed code</summary>
                        <pre className="mt-2 p-3 bg-slate-900 text-green-400 text-xs rounded-lg overflow-x-auto whitespace-pre-wrap">
                          {getEmbedCode(t.token)}
                        </pre>
                      </details>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  No form tokens yet. Create one to generate an embeddable lead form.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="mt-6">
            <Card className="bg-white border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Recent Activity ({activity.total})</CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchAll}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {activity.activity.length > 0 ? (
                  <div className="space-y-3">
                    {activity.activity.map((a, i) => (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${
                        a.type === 'new_lead' ? 'bg-emerald-50' : 'bg-amber-50'
                      }`} data-testid={`activity-${i}`}>
                        {a.type === 'new_lead' ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-amber-600 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">
                            {a.type === 'new_lead' ? `New lead captured` : `Duplicate detected`}
                            {a.lead_id && <span className="text-slate-500"> ({a.lead_id})</span>}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-500">
                            <span className="bg-white px-2 py-0.5 rounded-full capitalize">{a.source?.replace(/_/g, ' ')}</span>
                            <span>{a.phone}</span>
                            <span>{new Date(a.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-500">No webhook activity yet. Leads will appear here once they start coming in.</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IVR Setup Tab */}
          <TabsContent value="ivr" className="mt-6 space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">IVR / Incoming Call Integration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-slate-600">
                  Share this endpoint and payload format with your IVR service provider. No API key required — this is a public webhook.
                </p>

                {/* Endpoint */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Endpoint</label>
                  <div className="flex items-center gap-2 mt-1 bg-slate-50 rounded-lg p-3 font-mono text-sm">
                    <span className="text-emerald-600 font-semibold shrink-0">POST</span>
                    <span className="text-slate-800 flex-1 truncate" data-testid="ivr-endpoint-url">{BACKEND_URL}/api/ivr/incoming</span>
                    <button onClick={() => copyToClipboard(`${BACKEND_URL}/api/ivr/incoming`)} className="text-slate-400 hover:text-slate-700 shrink-0">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Payload Reference */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Payload (JSON)</label>
                  <div className="mt-1 bg-slate-900 rounded-lg p-4 text-sm font-mono text-slate-300 overflow-x-auto">
                    <pre className="whitespace-pre">{`{
  "phone": "9876543210",          // Required — caller's phone
  "assigned_to": "user@email.com",// Email or user_id — IVR decides assignment
  "name": "Amit Kumar",           // Caller name if collected
  "call_duration": 45,            // Duration in seconds
  "recording_url": "https://...", // Call recording URL
  "transcript": "Wants 2BHK...",  // Call transcript
  "project_interest": "High Park",// Project from IVR menu
  "source_campaign": "Google Ads",// Campaign tracking
  "ivr_option": "Press 1",        // Raw IVR option selected
  "caller_id": "022-12345678"     // IVR caller ID
}`}</pre>
                  </div>
                </div>

                {/* Field Reference Table */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Field Reference</label>
                  <div className="mt-1 border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm" data-testid="ivr-field-reference">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Field</th>
                          <th className="text-left px-4 py-2 font-medium">Required</th>
                          <th className="text-left px-4 py-2 font-medium">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[
                          ["phone", "Yes", "Caller's phone number"],
                          ["assigned_to", "No", "Email or user_id — lead gets assigned to this user. If same as current assignee, logged as incoming call."],
                          ["name", "No", "Caller name if IVR collected it"],
                          ["call_duration", "No", "Call duration in seconds"],
                          ["recording_url", "No", "URL to the call recording"],
                          ["transcript", "No", "Transcript of the conversation"],
                          ["project_interest", "No", "Project name mapped from IVR menu option"],
                          ["source_campaign", "No", "Campaign or ad source for tracking"],
                          ["ivr_option", "No", "Raw IVR menu option the caller selected"],
                          ["caller_id", "No", "Caller ID from the IVR system"],
                        ].map(([field, req, desc]) => (
                          <tr key={field}>
                            <td className="px-4 py-2 font-mono text-xs text-indigo-600">{field}</td>
                            <td className="px-4 py-2">{req === "Yes" ? <span className="text-red-600 font-medium">Yes</span> : <span className="text-slate-400">No</span>}</td>
                            <td className="px-4 py-2 text-slate-600">{desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Assignment Logic */}
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Assignment Logic</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li><strong>IVR-provided</strong> — if <code className="bg-blue-100 px-1 rounded text-xs">assigned_to</code> is set, the lead is assigned to that user</li>
                    <li><strong>Routing engine</strong> — if no assignment, CRM routing rules are applied</li>
                    <li><strong>Round-robin</strong> — falls back to distributing among active telecallers</li>
                  </ol>
                  <p className="text-xs text-blue-600 mt-3">
                    If the same user is already assigned, the call is logged as <strong>"Incoming call received"</strong> without re-assignment.
                  </p>
                </div>

                {/* Test Button */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick Test (cURL)</label>
                  <div className="mt-1 bg-slate-900 rounded-lg p-4 text-sm font-mono text-slate-300 overflow-x-auto">
                    <pre className="whitespace-pre text-xs">{`curl -X POST ${BACKEND_URL}/api/ivr/incoming \\
  -H "Content-Type: application/json" \\
  -d '{"phone":"9876543210","assigned_to":"your@email.com","call_duration":30}'`}</pre>
                  </div>
                  <button
                    className="mt-2 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    onClick={() => copyToClipboard(`curl -X POST ${BACKEND_URL}/api/ivr/incoming -H "Content-Type: application/json" -d '{"phone":"9876543210","assigned_to":"your@email.com","call_duration":30}'`)}
                  >
                    <Copy className="w-3 h-3" /> Copy cURL command
                  </button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default LeadCaptureSettingsPage;
